import jsPDF from 'jspdf';

/**
 * Generates and auto-downloads a styled fee receipt / invoice PDF.
 * No print dialog — uses jsPDF doc.save() for direct browser download.
 *
 * @param {object} invoice  - Invoice document from backend
 * @param {object} payment  - Optional latest payment record (for receipts)
 * @param {string} schoolName
 */
export function printFeeReceipt(invoice, payment = null, schoolName = 'School') {
  const isPaid   = invoice.status === 'paid';
  const isPartial = invoice.status === 'partial';
  const docTitle = isPaid || isPartial ? 'PAYMENT RECEIPT' : 'FEE INVOICE';

  // Accent colour: teal for paid, amber for pending, red for overdue
  const accent =
    isPaid    ? [13, 148, 136]  // teal-600
    : isPartial ? [37, 99, 235]  // blue-600
    : invoice.status === 'overdue' ? [220, 38, 38]  // red-600
    : [217, 119, 6]; // amber-600

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  const ML = 18; // margin left
  const MR = W - ML; // margin right
  let y = 0;

  const fmt = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN')}`;
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // ── Header band ──────────────────────────────────────────────
  doc.setFillColor(...accent);
  doc.rect(0, 0, W, 28, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolName, W / 2, 11, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(docTitle, W / 2, 18, { align: 'center' });

  // Receipt number + date pill (top right)
  doc.setFontSize(7);
  doc.text(`Ref: ${invoice.invoice_id?.slice(0, 8).toUpperCase()}`, MR, 7, { align: 'right' });
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, MR, 13, { align: 'right' });

  y = 36;

  // ── Status badge ─────────────────────────────────────────────
  const statusLabel = invoice.status?.toUpperCase() || 'PENDING';
  doc.setFillColor(...accent);
  doc.setDrawColor(...accent);
  doc.roundedRect(ML, y - 4, 28, 7, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(statusLabel, ML + 14, y + 0.5, { align: 'center' });

  y += 8;

  // ── Student info box ─────────────────────────────────────────
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(ML, y, W - 36, 24, 2, 2, 'FD');

  const col2 = ML + 58;
  const col3 = ML + 118;

  // Labels
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text('STUDENT NAME',   ML + 4, y + 6);
  doc.text('ADMISSION NO.',  col2,   y + 6);
  doc.text('CLASS / SECTION', col3,  y + 6);

  doc.text('MONTH / PERIOD', ML + 4, y + 16);
  doc.text('DUE DATE',       col2,   y + 16);
  doc.text('FEE TYPE',       col3,   y + 16);

  // Values
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.student_name || '-',        ML + 4, y + 12);
  doc.text(invoice.admission_number || '-',    col2,   y + 12);
  doc.text(`${invoice.class_name || ''} ${invoice.section || ''}`.trim() || '-', col3, y + 12);

  doc.setFontSize(8.5);
  doc.text(invoice.month || '-',               ML + 4, y + 22);
  doc.text(fmtDate(invoice.due_date),          col2,   y + 22);
  doc.text(invoice.is_additional ? (invoice.fee_type || 'Additional') : 'School Fee', col3, y + 22);

  y += 32;

  // ── Fee Breakdown table ───────────────────────────────────────
  // Table header
  doc.setFillColor(30, 41, 59);
  doc.rect(ML, y, W - 36, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text('FEE CATEGORY',  ML + 4, y + 5.5);
  doc.text('FREQUENCY',     ML + 80, y + 5.5, { align: 'center' });
  doc.text('AMOUNT',        MR - 18, y + 5.5, { align: 'right' });

  y += 9;

  const categories = invoice.categories || [];
  categories.forEach((cat, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(ML, y - 1, W - 36, 8, 'F');
    }
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(cat.name || '-',           ML + 4, y + 5);
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(cat.frequency || 'monthly', ML + 80, y + 5, { align: 'center' });
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.text(fmt(cat.amount), MR - 18, y + 5, { align: 'right' });

    doc.setDrawColor(241, 245, 249);
    doc.line(ML, y + 7, MR - 18, y + 7);
    y += 8;
  });

  y += 3;

  // ── Totals box ───────────────────────────────────────────────
  const boxW = 80;
  const boxX = MR - 18 - boxW;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(boxX, y, boxW + 18, 34, 2, 2, 'FD');

  const rowH = 8;
  const labelX = boxX + 4;
  const valX   = MR - 4;

  const tRow = (label, value, bold = false, color = [30, 41, 59]) => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(label, labelX, y + 5);
    doc.setTextColor(...color);
    doc.text(value, valX, y + 5, { align: 'right' });
    y += rowH;
  };

  tRow('Gross Amount',  fmt(invoice.gross_amount));
  if (invoice.discount > 0) tRow('Concession / Discount', `- ${fmt(invoice.discount)}`, false, [5, 150, 105]);
  tRow('Net Amount',    fmt(invoice.net_amount), true);
  tRow('Amount Paid',  fmt(invoice.paid_amount || 0), true, [5, 150, 105]);

  y += 6;

  // ── Payment details (if paid / partial) ───────────────────────
  if ((isPaid || isPartial) && payment) {
    doc.setFillColor(240, 253, 250);
    doc.setDrawColor(167, 243, 208);
    doc.roundedRect(ML, y, W - 36, 26, 2, 2, 'FD');

    doc.setTextColor(6, 95, 70);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYMENT DETAILS', ML + 4, y + 7);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    doc.text(`Transaction ID: ${payment.transaction_id || payment.payment_id?.slice(0, 12)?.toUpperCase() || '-'}`, ML + 4, y + 15);
    doc.text(`Method: ${(payment.payment_method || 'online').toUpperCase()}`, ML + 4, y + 22);

    doc.setTextColor(100, 116, 139);
    doc.text(`Paid on: ${fmtDate(payment.paid_at)}`, MR - 18, y + 15, { align: 'right' });
    doc.text(`Amount: ${fmt(payment.amount)}`, MR - 18, y + 22, { align: 'right' });

    y += 32;
  }

  y += 4;

  // ── Balance due banner (if not fully paid) ───────────────────
  const balance = (invoice.net_amount || 0) - (invoice.paid_amount || 0);
  if (balance > 0) {
    doc.setFillColor(...accent, 0.12);
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(...accent);
    doc.roundedRect(ML, y, W - 36, 14, 2, 2, 'FD');
    doc.setTextColor(...accent);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Balance Due: ${fmt(balance)}`, ML + 4, y + 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text(`Please pay by ${fmtDate(invoice.due_date)}`, MR - 18, y + 9, { align: 'right' });
    y += 20;
  }

  // ── Footer ───────────────────────────────────────────────────
  const footerY = 285;
  doc.setDrawColor(226, 232, 240);
  doc.line(ML, footerY, MR, footerY);
  doc.setTextColor(148, 163, 184);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on ${new Date().toLocaleDateString('en-IN')}`, ML, footerY + 5);
  doc.text('Powered by Ariskool', MR, footerY + 5, { align: 'right' });
  doc.text('This is a computer generated document and does not require a signature.', W / 2, footerY + 5, { align: 'center' });

  // ── Save ─────────────────────────────────────────────────────
  const filename = `FeeReceipt_${(invoice.student_name || 'Student').replace(/\s+/g, '_')}_${invoice.month?.replace(/\s+/g, '_') || ''}.pdf`;
  doc.save(filename);
}
